import {EBreakpoint} from '@/common/scripts/constants';
import {gsap} from 'gsap';
import Odometr from '@/components/page-loader/odometr';
import {Utils} from '@/common/scripts/utils';
import Context = gsap.Context;
import Conditions = gsap.Conditions;

const CLASS_FINISHED = 'page-loader-finished';
const DURATION = 1.6;
const DELAY = 0.5;

class PageLoader {
    private wrapper: HTMLElement;
    private counter: HTMLElement;
    private logo: HTMLElement;
    private cardsTop: HTMLElement;
    private cardsCenter: HTMLElement;
    private cardsBottom: HTMLElement;
    private neonTop: HTMLElement;
    private neonBottom: HTMLElement;
    private neonLeft: HTMLElement;
    private neonRight: HTMLElement;
    private odometr: Odometr;

    public init(wrapper: HTMLElement): void {
        this.wrapper = wrapper;
        this.counter = wrapper.querySelector('.j-page-loader-counter') as HTMLElement;
        this.logo = wrapper.querySelector('.j-page-loader__logo') as HTMLElement;
        this.cardsTop = wrapper.querySelector('.j-page-loader__cards-top') as HTMLElement;
        this.cardsCenter = wrapper.querySelector('.j-page-loader__cards-center') as HTMLElement;
        this.cardsBottom = wrapper.querySelector('.j-page-loader__cards-bottom') as HTMLElement;
        this.neonTop = wrapper.querySelector('.j-neon-top') as HTMLElement;
        this.neonBottom = wrapper.querySelector('.j-neon-bottom') as HTMLElement;
        this.neonLeft = wrapper.querySelector('.j-neon-left') as HTMLElement;
        this.neonRight = wrapper.querySelector('.j-neon-right') as HTMLElement;

        this.odometr = new Odometr();

        Utils.bodyFixed();

        this.odometr.init(this.counter, 100);
        this.animate();
    }

    private finish(): void {
        this.wrapper.classList.add(CLASS_FINISHED);

        Utils.bodyStatic();
    }

    // eslint-disable-next-line max-lines-per-function
    private animate(): void {
        const mm = gsap.matchMedia();

        mm.add({
            isDesktop: `(min-width: ${EBreakpoint.DESKTOP}px)`,
            isTablet : `(min-width: ${EBreakpoint.TABLET_PORTRAIT}px) and (max-width: ${EBreakpoint.DESKTOP - 1}px)`,
            isMobile : `(max-width: ${EBreakpoint.TABLET_PORTRAIT - 1}px)`
        },
        // eslint-disable-next-line max-lines-per-function
        (context: Context) => {
            const conditions: Conditions | null = context?.conditions || null;

            const tl = gsap.timeline({
                defaults: {
                    duration: DURATION,
                    delay   : DELAY,
                    ease    : `cubic-bezier(0.25, 0.1, 0.25, 1)`
                }
            });
            const counterTweenVars: gsap.TweenVars = {
                duration: DURATION,
                delay   : DELAY,
                ease    : `cubic-bezier(0.25, 0.1, 0.25, 1)`
            };

            const wrapperWidth: number = this.wrapper.getBoundingClientRect().width;
            const wrapperHeight: number = this.wrapper.getBoundingClientRect().height;

            tl.addLabel('first', 0)
                .to(this.cardsTop, {
                    x: this.calcCardWidth(conditions)
                }, 'first')
                .add(this.odometr.createTween(24, counterTweenVars), 'first')
                .to(this.neonBottom, {
                    x: -wrapperWidth - 10
                }, 'first')
                .addLabel('second', '>')
                .to(this.cardsBottom, {
                    x: this.calcCardWidth(conditions) * -1
                }, 'second')
                .add(this.odometr.createTween(54, counterTweenVars), 'second')
                .to(this.neonTop, {
                    x: wrapperWidth + 10
                }, 'second')
                .addLabel('third', `second+=${DURATION + DELAY}`)
                .to(this.cardsCenter, {
                    x: this.calcCenterCardsWidth(conditions)
                }, 'third')
                .to(this.logo, {
                    opacity: 1
                }, 'third')
                .add(this.odometr.createTween(100, counterTweenVars), 'third')
                .fromTo(this.neonTop, {
                    x(index: number, target: HTMLElement) {
                        return -(target.offsetWidth + 10);
                    }
                }, {
                    x: wrapperWidth + 10
                }, 'third')
                .fromTo(this.neonBottom, {
                    x(index: number, target: HTMLElement) {
                        return target.offsetWidth;
                    }
                }, {
                    x: -wrapperWidth - 10
                }, 'third')
                .to(this.neonLeft, {
                    y(index: number, target: HTMLElement) {
                        return target.offsetHeight + wrapperHeight;
                    }
                }, 'third')
                .to(this.neonRight, {
                    y(index: number, target: HTMLElement) {
                        return -(target.offsetHeight + wrapperHeight);
                    }
                }, 'third')
                .to(this.wrapper, {
                    yPercent  : -100,
                    duration  : 0.4,
                    onComplete: () => {
                        this.finish();
                    }
                });

            return () => {
                // optionally return a cleanup function that will be called when none of the conditions match anymore (after having matched)
                // it'll automatically call context.revert() - do NOT do that here . Only put custom cleanup code here.
            };
        });
    }

    private getSliceCount(conditions: Conditions | null): number {
        if (conditions?.['isDesktop']) {
            return 4;
        }
        if (conditions?.['isTablet']) {
            return 2;
        }

        return 1;
    }

    private calcCardWidth(conditions: Conditions | null): number {
        const isDesktop: boolean = conditions?.['isDesktop'] || false;
        const cards = [...this.cardsTop.querySelectorAll('.page-loader__card')] as HTMLElement[];
        const cardsCount = cards.length;
        const cardWidth = cards[0]?.getBoundingClientRect().width || 0;
        const sliceCount = this.getSliceCount(conditions);
        const delta = isDesktop ? 6 : 2;

        return (cardWidth * (cardsCount - sliceCount)) + (cardsCount - delta);
    }

    private calcCenterCardsWidth(conditions: Conditions | null): number {
        const isDesktop: boolean = conditions?.['isDesktop'] || false;
        const cards = [...this.cardsCenter.querySelectorAll('.page-loader__card')] as HTMLElement[];
        const cardsWidth = cards.reduce((acc: number, card: HTMLElement) => {
            return acc + card.getBoundingClientRect().width;
        }, 0);
        const cardsCount = cards.length;
        const cardWidth = cards[0]?.getBoundingClientRect().width || 0;

        const sliceCount = isDesktop ? 4 : 1;
        const delta = isDesktop ? 6 : 2;

        return cardsWidth - (cardWidth * sliceCount) + cardsCount - delta;
    }
}

export default PageLoader;
